/**
 * The rows of the score table and the axes of the scatter: how each metric is
 * read off a point, shown, and which direction is better. Deltas are always
 * against the reference the user picked on the page — there is no baseline.
 */

import type { EvaluatorKind } from "@/features/evaluators/types";
import { formatLatency, formatMoney, formatScore } from "@/lib/format";
import type { Comparison, ComparePoint, ItemChange } from "./types";

export type Tone = "better" | "worse" | "same";

export interface Metric {
  key: string;
  label: string;
  note: string;
  better: "high" | "low";
  get: (p: ComparePoint | undefined) => number | null;
  format: (v: number | null) => string;
  formatDelta: (diff: number, ref: number) => string;
  /** Evaluator rows: the version each experiment was scored with. */
  versions?: Record<string, number | null>;
  /** Versions differ, so a change against the reference would mislead. */
  mismatch?: boolean;
}

/** Older experiments may still carry a retired kind. */
const KIND_LABEL: Record<EvaluatorKind | "hybrid" | "custom", string> = {
  code: "Code",
  llm_judge: "LLM judge",
  hybrid: "Code + judge",
  custom: "Custom rule",
};

/** "+0.04", "−0.02", "±0.00" — a real minus sign, so columns line up. */
export function signed(diff: number, text: (abs: number) => string): string {
  const sign = Math.abs(diff) < 1e-9 ? "±" : diff > 0 ? "+" : "−";
  return `${sign}${text(Math.abs(diff))}`;
}

export function toneOf(diff: number, better: "high" | "low", threshold = 1e-9): Tone {
  if (Math.abs(diff) < threshold) return "same";
  return diff > 0 === (better === "high") ? "better" : "worse";
}

const scoreDelta = (d: number) => signed(d, (a) => a.toFixed(2));

/** " · v3" when every experiment used the same version. */
function versionNote(versions: Record<string, number | null> | undefined): string {
  const seen = [...new Set(Object.values(versions ?? {}).filter((v) => v !== null))];
  return seen.length === 1 ? ` · v${seen[0]}` : "";
}

export function scoreMetrics(c: Comparison): Metric[] {
  const overall: Metric = {
    key: "overall",
    label: "Overall",
    note: `mean of ${c.evaluators.length} evaluators`,
    better: "high",
    get: (p) => p?.overall ?? null,
    format: formatScore,
    formatDelta: scoreDelta,
  };
  return [
    overall,
    ...c.evaluators.map<Metric>((e) => ({
      key: e.key,
      label: e.name,
      note: e.version_mismatch
        ? "versions differ"
        : `${KIND_LABEL[e.kind as EvaluatorKind] ?? e.kind}${versionNote(e.versions)}`,
      better: "high",
      ...(e.versions ? { versions: e.versions } : {}),
      mismatch: e.version_mismatch ?? false,
      get: (p) => p?.scores[e.key] ?? null,
      format: formatScore,
      formatDelta: scoreDelta,
    })),
  ];
}

export const COST: Metric = {
  key: "cost",
  label: "Cost per item",
  note: "agent + evaluators",
  better: "low",
  get: (p) => p?.cost_per_item_usd ?? null,
  format: formatMoney,
  formatDelta: (d, ref) => (ref ? signed(d, (a) => `${Math.round((a / ref) * 100)}%`) : ""),
};

export const LATENCY: Metric = {
  key: "latency",
  label: "Latency p50",
  note: "per item",
  better: "low",
  get: (p) => p?.latency_p50_ms ?? null,
  format: formatLatency,
  formatDelta: (d) => signed(d, (a) => formatLatency(a)),
};

/** A change on Compare is five points or more on the item's mean score. */
export const ITEM_CHANGE = 0.05;

export type ItemFilter = "all" | "changed" | "worse" | "better";

/** Narrow a change to one evaluator's scores, and/or to one experiment against the reference. */
export interface ItemScope {
  evaluator?: string | null | undefined;
  vs?: string | null | undefined;
}

export function itemDirection(change: ItemChange, refId: string, scope: ItemScope = {}) {
  const meanOf = (r: ItemChange["results"][string] | undefined) =>
    scope.evaluator ? (r?.scores[scope.evaluator] ?? null) : (r?.mean ?? null);
  const ref = meanOf(change.results[refId]);
  const diffs = Object.entries(change.results)
    .filter(([id, r]) => id !== refId && (!scope.vs || id === scope.vs) && meanOf(r) !== null && ref !== null)
    .map(([, r]) => (meanOf(r) as number) - ref!);
  const edge = ITEM_CHANGE - 1e-9; // 0.95 − 0.90 is 0.04999… in floating point
  const worse = diffs.some((d) => d <= -edge);
  const better = diffs.some((d) => d >= edge);
  return { worse, better, changed: worse || better };
}

export function keepItem(
  change: ItemChange,
  refId: string,
  filter: ItemFilter,
  scope: ItemScope = {},
): boolean {
  if (filter === "all") return true;
  return itemDirection(change, refId, scope)[filter];
}

/** How many items one experiment got better / worse on one evaluator, against the reference. */
export function changeCounts(data: Comparison, refId: string, expId: string, evaluator: string) {
  const scope = { evaluator, vs: expId };
  const dirs = data.item_changes.map((c) => itemDirection(c, refId, scope));
  return { better: dirs.filter((d) => d.better).length, worse: dirs.filter((d) => d.worse).length };
}
